import React from 'react';
import { useTimeoutFn } from 'react-use';

interface DelayedCallResponse {
    /**
     * This will make the callback function execute in <delay>ms
     * if call(..) is used while another call(..) is in progress, the previous one will be overridden
     */
    call: (delay: number) => void;
    /**
     * cancels the call(..); if no call(..) is in progress, this is a noop
     */
    cancel: () => void;
}

export const useDelayedCall = (callback: () => void): DelayedCallResponse => {
    const [delay, setDelay] = React.useState<number>();
    // const callbackRef = React.useRef(callback);
    const [isTriggered, setIsTriggered] = React.useState<boolean>(false);

    const trigger = React.useCallback(() => {
        setIsTriggered(true);
    }, []);

    // this hook always executes no matter what the delay is
    // therefore make it execute an empty function if delay == undefined
    const [isReady, cancel, set] = useTimeoutFn(
        (delay && trigger) || (() => {}),
        delay,
    );

    React.useEffect(() => {
        if (isTriggered && isReady()) {
            callback();
            setDelay(undefined);
            setIsTriggered(false);
        }
    }, [isReady, isTriggered, callback]);

    const retVal = React.useMemo(
        () => ({
            call: (delay: number) => {
                setDelay(delay);
                set();
            },
            cancel,
        }),
        [cancel, set],
    );

    return retVal;
};
